import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Button, Chip, Fade, InputAdornment, OutlinedInput, Paper, Stack, Typography } from '@mui/material';
import { DetailAndDelBtn, MetrcLogo, NONE, noPermTablePrefixMsg } from '../../utility';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import SearchIcon from '@mui/icons-material/Search';
import { metrcTagsClasses, roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import { FarmContext } from '../../../context/FarmContext';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import { useToken } from '../../../utility';
import { failsTokenAndSetupCheck } from '../../../checks/setup';

export default function _UnitTab(props) {
  const { farmContextValue } = useContext(FarmContext);

  const {
    currDarkThemeClass = '',
    openStrainsPopup = false,
    categoryPerms = null,
    handleSetValues = () => {},
    handleErrorWithFeedback = () => {},
    handleEntityEntryCreateRes = () => {},
    handleEntityEntryModifyRes = () => {},
    ...otherProps
  } = props;
  const [open, setOpen] = useState(false);
  const [detailChildren, setDetailChildren] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [loading, setLoading] = useState(false);
  const [farms, setFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const feedbackCX = useContext(FeedbackContext);
  const { token, tokenDetails } = useToken();
  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token]);

  const fetchFarmData = async (currentPage = page, limit = rowsPerPage, search = searchTerm) => {
    setLoading(true);
    try {
      let url = `unit/?limit=${rowsPerPage}&page=${page + 1}`;
      if (searchTerm) {
        url += `&name__icontains=${encodeURIComponent(searchTerm)}`;
      }
      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      handleErrorWithFeedback(errFeedbackFn({}))(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (invalidTokenState) return;

    fetchFarmData(page, rowsPerPage, searchTerm, invalidTokenState);
  }, [page, rowsPerPage, searchTerm, invalidTokenState]);

  const formatData = formattedData => {
    return formattedData.map((item, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      ...unitKeyTitleMap.map(e => {
        const val = item[e.key];
        if (typeof val === 'boolean') return val ? 'True' : 'False';
        return val ?? 'N/A';
      }),
      <DetailAndDelBtn {...gDItemEntryActionBtnProps(item)} />,
    ]);
  };
  const handleEntityEntryModify = (entity, prop) => async value => {
    const errConfig = {
      modify: true,
      resource: 'Strain',
    };
    let res = { success: false };
    try {
      res = await apiService.modifyInstance(entity, value).then(handleEntityEntryModifyRes(prop));
      fetchFarmData();
    } catch (err) {
      handleErrorWithFeedback(errFeedbackFn(errConfig))(err);
    }
    return res;
  };
  const handleSetSelItem = (prop, value) => {
    setDetailChildren(state => ({
      ...state,
      selStrain: {
        ...state.selStrain,
        [prop]: value,
      },
    }));
  };

  const handleSyncToMetrc = async () => {
    setSyncLoading(true);

    try {
      const response = await apiService.fetchInstance('unit/?sync_to_metrc=true');

      if (response.status === 200) {
        // 👇 REPLACE with your unit-fetching function
        fetchFarmData(); // or fetchUnitData(), loadUnitTable(), etc.

        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Units Synced From Metrc Successfully.',
        });
        setTimeout(() => {
          feedbackCX.setContextValue(false, {});
        }, 5000);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error syncing units from Metrc.',
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const gDItemEntryActionBtnProps = category => ({
    currDarkThemeClass,
    toShowDelBtn: false,
    onDetailClick: () => handleItemEntryView(category),
    toShowDetailBtn: false,
  });

  const handleItemEntryView = category => {
    handleSetValues(updateKey, true);
    setOpen(true);

    setDetailChildren({
      selStrain: category,
      title: category.category_name,
      toShowSave: categoryPerms?.update ?? false,
      handleCategorySave: handleEntityEntryModify(`category/${category.id}/`, 'category'),
      handleSetSelItem,
    });
  };

  // Define your title map for the table headers
  const unitKeyTitleMap = [
    { key: 'unit_name', title: 'Unit Name' },
    { key: 'unit_type', title: 'Unit Type' },
    { key: 'abbreviation', title: 'Abbreviation' },
  ];
  const updateKey = 'update';
  const tableTitles = unitKeyTitleMap.map(e => ({ title: e.title }));

  // Functions for pagination (replace with actual logic)
  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
    // Update page and rowsPerPage (replace with state management)
  };
  const displayProps = {
    currDarkThemeClass,
    tableEntries: data,
    tableTitles,
    totalCount,
    onPageChange: handlePageChange,
    toShowAddBtn: false,
    loading,
    rowsPerPage,
    page,
    entity: 'Items',
    ...otherProps,
  };

  // Example values for the table props
  const unitTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Unit Found (Please click on Sync From Metrc)',
    entity: 'Waste',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...unitKeyTitleMap.map(e => ({ title: e.title })), { title: '' }],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading: false, // Set loading to true if data is being fetched
    onClick: () => console.log('Add New Item Clicked'), // Define the add button functionality
    ...otherProps, // Spread any additional props passed from parent
  };

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
  const metrcTagFilterProps = {
    direction: 'row',
    spacing: 3,
    alignItems: 'center',
  };
  const metrcTagFilterTypoProps = {
    className: `${metrcTagsClasses?.filterTitleTypo} ${currDarkThemeClass}`,
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  const handleKeyPress = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Item Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <Stack {...metrcTagFilterProps}></Stack>
          {/* <Stack direction='row' sx={{justifyContent: 'flex-end'}}>
               
                <OutlinedInput {...outlinedInputProps}/> 
            
            </Stack> */}
          <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSyncToMetrc}
              disabled={syncLoading}
              style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
              sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
            >
              <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
              {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
            </Button>

            <OutlinedInput {...outlinedInputProps} />
          </Stack>
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...unitTableValues} />
        </Box>
      </Box>
    </Fade>
  );
}
